import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Calendar } from "lucide-react";
import { loadProjectList } from "@/lib/content";
import { breadcrumbSchema } from "@/lib/schema";
import { Rise } from "@/components/motion/Reveal";
import useSEO from "@/hooks/useSEO";

const ALL = "All work";

export default function ProjectsList() {
  const [projects, setProjects] = useState(null);
  const [filter, setFilter] = useState(ALL);

  useSEO({
    title: "Projects — ElectroMax Case Studies",
    description: "EV charger installs, panel upgrades, commercial retrofits, and emergency call-outs from ElectroMax electricians across Metro Vancouver.",
    jsonld: breadcrumbSchema([
      { name: "Home", href: "/" },
      { name: "Projects", href: "/projects" },
    ]),
  });

  useEffect(() => {
    loadProjectList().then(setProjects).catch(() => setProjects([]));
  }, []);

  const serviceTypes = useMemo(() => {
    if (!projects) return [ALL];
    const set = new Set(projects.map((p) => p.serviceType).filter(Boolean));
    return [ALL, ...Array.from(set)];
  }, [projects]);

  const filtered = useMemo(() => {
    if (!projects) return [];
    if (filter === ALL) return projects;
    return projects.filter((p) => p.serviceType === filter);
  }, [projects, filter]);

  return (
    <div data-testid="projects-list-page" className="bg-[#0A0A0A] text-white min-h-screen">
      {/* Header */}
      <section className="relative pt-36 md:pt-44 pb-16 overflow-hidden grain">
        <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] bg-[#E60000]/10 blur-[120px] rounded-full" />
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-5 inline-flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#E60000]" />
            Recent Work
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.95] tracking-tighter font-black max-w-4xl"
          >
            The jobs
            <br />
            <span className="text-white/40">on the truck.</span>
          </motion.h1>
          <p className="mt-6 max-w-2xl text-white/70 leading-relaxed text-base md:text-lg">
            Case studies from Metro Vancouver homes, duplexes, and light commercial builds.
            Every entry has the challenge, the fix, and the actual outcome — no marketing gloss.
          </p>
        </div>
      </section>

      {/* Filters */}
      {projects && serviceTypes.length > 1 && (
        <section className="border-t border-[#262626] bg-[#0F0F0F]">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-4 flex flex-wrap gap-2">
            {serviceTypes.map((t) => (
              <button
                key={t}
                onClick={() => setFilter(t)}
                data-testid={`project-filter-${t.replace(/\s+/g, "-").toLowerCase()}`}
                className={`text-xs tracking-[0.18em] uppercase font-bold px-4 py-2.5 border transition-colors ${
                  filter === t
                    ? "bg-[#E60000] border-[#E60000] text-white"
                    : "border-[#262626] text-white/70 hover:border-white/60 hover:text-white"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="border-t border-[#262626] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
          {projects === null && (
            <div className="text-white/50" data-testid="projects-loading">Loading projects…</div>
          )}
          {projects && projects.length === 0 && (
            <div className="text-white/50" data-testid="projects-empty">No projects published yet.</div>
          )}
          {projects && filtered.length === 0 && projects.length > 0 && (
            <div className="text-white/50" data-testid="projects-no-match">No projects in this category yet.</div>
          )}
          {filtered.length > 0 && (
            <Rise>
              <motion.div
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
              >
                {filtered.map((p) => (
                  <motion.div
                    key={p.slug}
                    variants={{
                      hidden: { opacity: 0, y: 24 },
                      show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
                    }}
                  >
                    <Link
                      to={`/projects/${p.slug}`}
                      data-testid={`project-card-${p.slug}`}
                      className="group flex flex-col border border-[#262626] bg-[#0F0F0F] hover:border-[#E60000] transition-colors overflow-hidden h-full"
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        {p.featureImage ? (
                          <img src={p.featureImage} alt={`${p.title} — ${p.serviceType} in ${p.location} by ElectroMax`} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                        ) : (
                          <div className="w-full h-full bg-[#141414]" />
                        )}
                      </div>
                      <div className="p-5 md:p-6 flex flex-col grow">
                        <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold">
                          {p.serviceType}
                        </div>
                        <h3 className="mt-2 font-display text-xl md:text-2xl tracking-tight font-bold leading-tight">
                          {p.title}
                        </h3>
                        <div className="mt-3 flex items-center gap-4 text-[11px] text-white/50">
                          {p.location && (
                            <span className="inline-flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.location}</span>
                          )}
                          {p.completedYear && (
                            <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {p.completedMonth} {p.completedYear}</span>
                          )}
                        </div>
                        <p className="mt-4 text-sm text-white/60 line-clamp-3 leading-relaxed">
                          {p.description}
                        </p>
                        <div className="mt-auto pt-5 flex items-center justify-end">
                          <ArrowUpRight className="w-5 h-5 text-white/40 group-hover:text-[#E60000] transition-colors" strokeWidth={2.5} />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </Rise>
          )}
        </div>
      </section>
    </div>
  );
}
